import React from 'react';
import { 
  UserProfile, 
  FinancialConfig, 
  Quote, 
  Client, 
  Supplier, 
  WorkOrder, 
  Receipt, 
  Appointment, 
  StaffMember, 
  SubscriptionClubPlan, 
  StorefrontProfile, 
  SimpleService, 
  AppView, 
  QuoteStatus 
} from '../../types';
import { WorkOrderStatus } from '../../modules/erp/types';
import ClientManager from '../../modules/erp/components/ClientManager';
import SupplierManager from '../../modules/erp/components/SupplierManager';
import WorkOrderCreator from '../../modules/erp/components/WorkOrderCreator';
import ReceiptManager from '../../modules/erp/components/ReceiptManager';
import AppointmentCalendar from '../../modules/erp/components/AppointmentCalendar';
import SignatureDocumentCenter from '../../modules/erp/components/SignatureDocumentCenter';
import MarketIntelligenceRadar from '../../modules/erp/components/MarketIntelligenceRadar';
import ExecutiveCockpitDashboard from '../../modules/erp/components/ExecutiveCockpitDashboard';
import PlatformManagementDashboard from '../admin/PlatformManagementDashboard';
import ManagementDashboardHybrid from '../../modules/erp/components/ManagementDashboardHybrid';
import ErpHubDashboard from '../../modules/erp/components/ErpHubDashboard';
import DirectBookingPage, { DirectBookingService } from '../../modules/erp/components/DirectBookingPage';
import { PipelineItem } from '../../modules/erp/components/WorkPipelineKanban';
import { EnterpriseVerticalId } from '../../modules/erp/enterprise/config/enterpriseVerticals';
import { useTeamPermissions } from '../../contexts/TeamPermissionsContext';
import { PaywallInitialPlan } from '../PaywallModal';
import { Lock, Zap } from 'lucide-react';
import { resolveCompanyLogo } from '../../hooks/useCompanyProfile';

export interface SpecialistViewDispatcherProps {
  view: AppView;
  user: UserProfile | null;
  config: FinancialConfig | null;
  clients: Client[];
  suppliers: Supplier[];
  workOrders: WorkOrder[];
  receipts: Receipt[];
  appointments: Appointment[];
  staffMembers: StaffMember[];
  subscriptionClubs: SubscriptionClubPlan[];
  storefrontProfile: StorefrontProfile | null;
  quotes: Quote[];
  services: SimpleService[];
  initialReceiptDraft?: Partial<Receipt>;
  setInitialReceiptDraft: (draft?: Partial<Receipt>) => void;
  currentMonthQuotesCount: number;
  isEnterpriseUser: boolean;
  enterpriseVertical: EnterpriseVerticalId;
  renderWrapper?: (content: React.ReactNode) => React.ReactNode;
  onGoBack: () => void;
  onNavigate: (view: AppView) => void;
  onOpenChat: () => void;
  onOpenMaterials: () => void;
  onOpenClients: () => void;
  onOpenSuppliers: () => void;
  onOpenWorkOrders: () => void;
  onOpenReceipts: (draft?: Partial<Receipt>) => void;
  onOpenCalendar: () => void;
  onOpenDirectBooking: () => void;
  onOpenSignatures: () => void;
  onOpenMarketRadar: () => void;
  onOpenExecutiveCockpit: () => void;
  onOpenPipeline: () => void;
  onOpenProfile: () => void;
  onOpenTeamManagement: () => void;
  onOpenEstoqueHub: () => void;
  onOpenProntuarioSoap: () => void;
  onOpenRetornoVacinas: () => void;
  onOpenCofreExames: () => void;
  onOpenPortalContador: () => void;
  onOpenHistory: () => void;
  onSaveClient: (client: Client) => Promise<any>;
  onDeleteClient: (id: string) => Promise<void>;
  onSaveSupplier: (supplier: Supplier) => Promise<any>;
  onDeleteSupplier: (id: string) => Promise<void>;
  onSaveWorkOrder: (wo: WorkOrder) => Promise<any>;
  onDeleteWorkOrder: (id: string) => Promise<void>;
  onSaveReceipt: (receipt: Receipt) => Promise<any>;
  onDeleteReceipt: (id: string) => Promise<void>;
  onSaveAppointment: (appt: any) => Promise<any>;
  onDeleteAppointment: (id: string) => Promise<void>;
  onUpdateAppointmentStatus: (id: string, status: any) => Promise<void>;
  onRefreshAppointments?: () => Promise<any> | void;
  onSaveStaffMember: (staff: StaffMember) => Promise<any>;
  onDeleteStaffMember: (id: string) => Promise<void>;
  onSaveSubscriptionClub: (club: any) => Promise<any>;
  onDeleteSubscriptionClub: (id: string) => Promise<void>;
  onSaveSignature: (sig: any) => Promise<any>;
  onSaveQuote: (quote: Quote) => Promise<any>;
  onSaveStorefrontProfile?: (profile: StorefrontProfile) => Promise<any> | void;
  onUpdateConfigRate: (rate: number) => Promise<void>;
  onLogout: () => Promise<void>;
  onOpenPaywall: (plan?: PaywallInitialPlan) => void;
  canAccessTier: (tier: 'free' | 'pro' | 'premium' | 'enterprise') => boolean;
  handleActionClick: (tier: 'free' | 'pro' | 'premium' | 'enterprise', action: () => void, name: string, desc?: string) => void;
  setAppointments: React.Dispatch<React.SetStateAction<Appointment[]>>;
}

export const SpecialistViewDispatcher: React.FC<SpecialistViewDispatcherProps> = ({
  view,
  user,
  config,
  clients,
  suppliers,
  workOrders,
  receipts,
  appointments,
  staffMembers,
  subscriptionClubs,
  storefrontProfile,
  quotes,
  services,
  initialReceiptDraft,
  setInitialReceiptDraft,
  currentMonthQuotesCount,
  isEnterpriseUser,
  enterpriseVertical,
  renderWrapper,
  onGoBack,
  onNavigate,
  onOpenChat,
  onOpenMaterials,
  onOpenClients,
  onOpenSuppliers,
  onOpenWorkOrders,
  onOpenReceipts,
  onOpenCalendar,
  onOpenDirectBooking,
  onOpenSignatures,
  onOpenMarketRadar,
  onOpenExecutiveCockpit,
  onOpenPipeline,
  onOpenProfile,
  onOpenTeamManagement,
  onOpenEstoqueHub,
  onOpenProntuarioSoap,
  onOpenRetornoVacinas,
  onOpenCofreExames,
  onOpenPortalContador,
  onOpenHistory,
  onSaveClient,
  onDeleteClient,
  onSaveSupplier,
  onDeleteSupplier,
  onSaveWorkOrder,
  onDeleteWorkOrder,
  onSaveReceipt,
  onDeleteReceipt,
  onSaveAppointment,
  onDeleteAppointment,
  onUpdateAppointmentStatus,
  onRefreshAppointments,
  onSaveStaffMember,
  onDeleteStaffMember,
  onSaveSubscriptionClub,
  onDeleteSubscriptionClub,
  onSaveSignature,
  onSaveQuote,
  onSaveStorefrontProfile,
  onUpdateConfigRate,
  onLogout,
  onOpenPaywall,
  canAccessTier,
  handleActionClick,
  setAppointments,
}) => {
  const { can } = useTeamPermissions();
  const wrap = (node: React.ReactNode) => (isEnterpriseUser && renderWrapper ? renderWrapper(node) : node);

  if (view === 'erp-hub' && user && config && !isEnterpriseUser) {
    const resolvedLogo = resolveCompanyLogo(user, storefrontProfile);
    return (
      <ErpHubDashboard
        demoProfile={storefrontProfile ? {
          ...storefrontProfile,
          logoUrl: storefrontProfile.logoUrl || resolvedLogo
        } : {
          id: 'prof_' + user.id,
          userId: user.id,
          slug: user.businessName ? user.businessName.toLowerCase().replace(/[^a-z0-9]/g, '-') : 'meu-negocio',
          displayName: user.businessName || user.ownerName,
          tagline: user.profession || 'Atendimento Profissional',
          isPublished: true,
          viewCount: 0,
          nicheBadges: [user.niche || 'Geral'],
          whatsappNumber: user.phone || '',
          portfolioItems: [],
          logoUrl: resolvedLogo
        }}
        userNiche={user.niche}
        userProfession={user.profession}
        userId={user.id}
        simulatedPlanTier={(user.plan === 'Especialista' || user.isTrialActive) ? 'premium' : user.plan === 'Pro' ? 'pro' : 'free'}
        calculatedVhbRate={config.baseHourValue || 50}
        canAccess={canAccessTier}
        handleActionClick={handleActionClick}
        quotesCount={currentMonthQuotesCount}
        accessEndsAt={user.accessEndsAt}
        trialEndsAt={user.trialEndsAt}
        isTrialActive={user.isTrialActive}
        userName={user.ownerName || user.fullName}
        appointmentsCount={(appointments || []).filter(a => a.scheduledDate === new Date().toISOString().slice(0, 10)).length}
        weeklyRevenueEstimate={(receipts || []).filter(r => r.status !== 'canceled').reduce((acc, r) => acc + (r.amountPaid || 0), 0)}
        onOpenPaywall={(plan) => {
          onOpenPaywall(plan || (user?.plan === 'Pro' ? 'specialist' : 'annual'));
        }}
        onLogout={onLogout}
        onNavigate={(target) => {
          if (target === 'orcamento') onOpenChat();
          else if (target === 'custos') onOpenMaterials();
          else if (target === 'clientes') onOpenClients();
          else if (target === 'fornecedores') onOpenSuppliers();
          else if (target === 'os') onOpenWorkOrders();
          else if (target === 'recibo') onOpenReceipts();
          else if (target === 'agenda') onOpenCalendar();
          else if (target === 'agendar') onOpenDirectBooking();
          else if (target === 'assinatura') onOpenSignatures();
          else if (target === 'radar') onOpenMarketRadar();
          else if (target === 'cockpit') onOpenExecutiveCockpit();
          else if (target === 'gestao') onOpenPipeline();
          else if (target === 'cadastro') onOpenProfile();
          else if (target === 'equipe') onOpenTeamManagement();
          else if (target === 'estoque_hub') onOpenEstoqueHub();
          else if (target === 'prontuario_soap') onOpenProntuarioSoap();
          else if (target === 'retorno_vacinas') onOpenRetornoVacinas();
          else if (target === 'cofre_exames') onOpenCofreExames();
          else if (target === 'portal_contador') onOpenPortalContador();
        }}
      />
    );
  }

  // Proteção contra deep-linking direto via URL para módulos do Plano Especialista
  const specialistViews: AppView[] = [
    'clients',
    'suppliers',
    'work-orders',
    'receipts',
    'calendar',
    'signatures',
    'market-radar',
    'executive-cockpit',
    'pipeline',
    'direct-booking'
  ];

  if (specialistViews.includes(view) && !canAccessTier('premium')) {
    const featureLabels: Record<string, string> = {
      clients: 'CRM de Clientes & GPS',
      suppliers: 'Gestão de Fornecedores & CNPJ',
      'work-orders': 'Ordens de Serviço com Fotos',
      receipts: 'Recibos PIX Profissionais',
      calendar: 'Agenda Multi-Atendentes',
      signatures: 'Assinatura Digital na Tela',
      'market-radar': 'Radar de Mercado & Preço Médio',
      'executive-cockpit': 'Painel Financeiro & Cockpit DRE',
      pipeline: 'Quadro de Tarefas Operacionais',
      'direct-booking': 'Vitrine Pública de Agendamento'
    };

    return wrap(
      <div className="max-w-xl mx-auto py-12 px-4 font-sans text-center">
        <div className="bg-white rounded-3xl border border-zinc-200 shadow-xl p-8 space-y-6">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 text-amber-600 mx-auto flex items-center justify-center border border-amber-200 shadow-xs">
            <Lock className="w-8 h-8 text-amber-600" />
          </div>
          <div className="space-y-2">
            <span className="text-[10px] font-black tracking-widest uppercase bg-amber-100 text-amber-800 px-3 py-1 rounded-full">
              Plano Especialista
            </span>
            <h2 className="text-xl font-black text-zinc-900">
              {featureLabels[view] || 'Módulo do Plano Especialista'}
            </h2>
            <p className="text-xs text-zinc-500 max-w-sm mx-auto leading-relaxed">
              Este recurso faz parte do Plano Especialista do R-MAX. Desbloqueie gestão de clientes, agenda, recibos PIX e ordens de serviço.
            </p>
          </div>
          <div className="space-y-2.5 pt-2">
            <button
              type="button"
              onClick={() => onOpenPaywall('specialist')}
              className="w-full py-3.5 px-6 bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-xs uppercase tracking-wider rounded-2xl shadow-lg transition-all cursor-pointer active:scale-95 flex items-center justify-center gap-2"
            >
              <Zap className="w-4 h-4 text-amber-400" />
              <span>Conhecer o Plano Especialista</span>
            </button>
            <button
              type="button"
              onClick={onGoBack}
              className="w-full py-3 px-6 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-bold text-xs uppercase tracking-wider rounded-2xl transition-colors cursor-pointer"
            >
              Voltar ao Início
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'clients' && user) {
    const content = (
      <ClientManager
        clients={clients}
        onSaveClient={onSaveClient}
        onDeleteClient={onDeleteClient}
        onBack={onGoBack}
      />
    );
    return wrap(content);
  }

  if (view === 'suppliers' && user) {
    if (!can('view_suppliers')) {
      return (
        <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4 font-sans">
          <div className="max-w-md w-full bg-white border border-zinc-200 rounded-3xl p-6 text-center space-y-4 shadow-xl">
            <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 mx-auto flex items-center justify-center border border-amber-200">
              <Lock className="w-6 h-6" />
            </div>
            <h3 className="text-base font-black text-zinc-900">Acesso Restrito pelo Gestor</h3>
            <p className="text-xs text-zinc-500 leading-relaxed">
              A gestão de fornecedores e compras é restrita à administração da empresa.
            </p>
            <button
              type="button"
              onClick={onGoBack}
              className="w-full py-2.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 font-bold text-xs rounded-xl transition-colors cursor-pointer"
            >
              Voltar
            </button>
          </div>
        </div>
      );
    }
    const content = (
      <SupplierManager
        suppliers={suppliers}
        onSaveSupplier={onSaveSupplier}
        onDeleteSupplier={onDeleteSupplier}
        onBack={onGoBack}
      />
    );
    return wrap(content);
  }

  if (view === 'work-orders' && user) {
    const content = (
      <WorkOrderCreator
        workOrders={workOrders}
        nicheKey={
          `${user?.niche || ''} ${user?.profession || ''}`.toLowerCase().match(/celular|smartphone|telefon/)
            ? 'assistencia_celular'
            : (isEnterpriseUser && enterpriseVertical === 'technical_service' ? 'mecanica' : undefined)
        }
        providerName={user.businessName || user.ownerName}
        onSave={onSaveWorkOrder}
        onDeleteWorkOrder={onDeleteWorkOrder}
        onBack={onGoBack}
        onOpenReceipt={(wo) => {
          onOpenReceipts({
            clientName: wo.clientName,
            clientDocument: wo.clientDocument,
            description: `Ordem de Serviço Nº ${wo.orderNumber}: ${wo.title}`.trim(),
            totalServiceValue: wo.finalPrice || 0,
            amountPaid: wo.finalPrice || 0,
            workOrderId: wo.id,
            warrantyTerms: wo.warrantyTerms
          });
        }}
      />
    );
    return wrap(content);
  }

  if (view === 'receipts' && user) {
    const content = (
      <ReceiptManager
        user={user}
        receipts={receipts}
        initialReceipt={initialReceiptDraft}
        providerName={user.businessName || user.ownerName}
        providerDocument={user.document || ''}
        providerPixKey={user.pixKey || ''}
        providerCity={user.city || 'São Paulo'}
        onSaveReceipt={onSaveReceipt}
        onDeleteReceipt={onDeleteReceipt}
        onBack={() => {
          setInitialReceiptDraft(undefined);
          onGoBack();
        }}
      />
    );
    return wrap(content);
  }

  if (view === 'calendar' && user) {
    const fallbackSlug = (user.businessName || user.ownerName || 'agendamento')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    const handleSaveBusinessHours = async (hours: string) => {
      if (onSaveStorefrontProfile) {
        const base: StorefrontProfile = storefrontProfile || {
          id: 'prof_' + user.id,
          userId: user.id,
          slug: fallbackSlug || 'agendamento',
          displayName: user.businessName || user.ownerName || 'Meu Negócio',
          nicheBadges: [],
          whatsappNumber: user.phone || '',
          isPublished: true,
          viewCount: 0,
        };
        await onSaveStorefrontProfile({
          ...base,
          businessHours: hours
        });
      }
    };

    const content = (
      <AppointmentCalendar
        providerName={user.businessName || user.ownerName}
        providerPixKey={user.pixKey || ''}
        providerCity={user.city || 'São Paulo'}
        logoUrl={resolveCompanyLogo(user, storefrontProfile)}
        accentColor={storefrontProfile?.accentColor || '#10b981'}
        slug={storefrontProfile?.slug || fallbackSlug || 'agendamento'}
        vertical={isEnterpriseUser ? enterpriseVertical : undefined}
        appointments={appointments}
        staffMembers={staffMembers}
        subscriptionClubs={subscriptionClubs}
        services={services}
        businessHours={storefrontProfile?.businessHours || 'Terça a Sábado: 08:00 às 19:00'}
        onSaveBusinessHours={handleSaveBusinessHours}
        onSaveAppointment={onSaveAppointment}
        onUpdateStatus={onUpdateAppointmentStatus}
        onRefreshAppointments={onRefreshAppointments}
        onDeductPackageSession={async (id) => {
          setAppointments(prev => prev.map(a => a.id === id ? { ...a, packageCurrentSession: Math.min((a.packageTotalSessions || 1), (a.packageCurrentSession || 1) + 1) } : a));
        }}
        onSaveStaffMember={onSaveStaffMember}
        onDeleteStaffMember={onDeleteStaffMember}
        onSaveSubscriptionClub={onSaveSubscriptionClub}
        onDeleteSubscriptionClub={onDeleteSubscriptionClub}
        onOpenDirectBooking={onOpenDirectBooking}
        onOpenReceipt={(appt) => {
          onOpenReceipts({
            clientName: appt.clientName,
            description: `Atendimento: ${appt.serviceName || 'Serviço agendado'}`,
            totalServiceValue: appt.price || 0,
            amountPaid: appt.price || 0
          });
        }}
        onBack={onGoBack}
      />
    );
    return wrap(content);
  }

  if (view === 'signatures' && user) {
    const content = (
      <SignatureDocumentCenter
        providerName={user.businessName || user.ownerName}
        clients={clients}
        quotes={quotes}
        workOrders={workOrders}
        onSaveSignature={onSaveSignature}
        onSaveWorkOrder={onSaveWorkOrder}
        onBack={onGoBack}
      />
    );
    return wrap(content);
  }

  if (view === 'market-radar' && user && config) {
    const content = (
      <MarketIntelligenceRadar
        userCity={user.city || 'São Paulo'}
        userState={user.state || 'SP'}
        userNiche={user.niche || 'Geral'}
        currentVhbRate={config.baseHourValue || 50}
        onApplyNewVhbRate={onUpdateConfigRate}
        onBack={onGoBack}
      />
    );
    return wrap(content);
  }

  if (view === 'executive-cockpit' && config) {
    if (!can('view_executive_cockpit')) {
      return (
        <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4 font-sans">
          <div className="max-w-md w-full bg-white border border-zinc-200 rounded-3xl p-6 text-center space-y-4 shadow-xl">
            <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 mx-auto flex items-center justify-center border border-rose-200">
              <Lock className="w-6 h-6" />
            </div>
            <h3 className="text-base font-black text-zinc-900">Acesso Restrito pelo Gestor</h3>
            <p className="text-xs text-zinc-500 leading-relaxed">
              O Cockpit Executivo e Faturamento Consolidado é restrito à diretoria da empresa.
            </p>
            <button
              type="button"
              onClick={onGoBack}
              className="w-full py-2.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 font-bold text-xs rounded-xl transition-colors cursor-pointer"
            >
              Voltar
            </button>
          </div>
        </div>
      );
    }
    const content = (
      <ExecutiveCockpitDashboard
        monthlyFixedCosts={config.monthlyFixedCost || 1500}
        proLaboreGoal={config.proLaboreGoal || 3000}
        currentVhbRate={config.baseHourValue || 50}
        monthlyCapacityHours={(config.weeklyWorkHours || 40) * 4}
        proposals={quotes.map(q => ({
          id: q.id,
          code: q.id.substring(0, 6).toUpperCase(),
          clientName: q.clientName,
          title: q.rawServiceName,
          finalPrice: q.finalPrice || q.finalSuggestedPrice || 0,
          materialsCost: q.materialCost || 0,
          laborCost: (q.finalPrice || 0) - (q.materialCost || 0),
          stage: q.status || 'rascunho',
          nicheKey: 'geral',
          createdAt: q.createdAt
        }))}
        workOrders={workOrders}
        receipts={receipts}
        onBack={onGoBack}
        onNavigateToCostCenter={onOpenMaterials}
        onNavigateToQuotes={onOpenHistory}
        onNavigateToCalendar={onOpenCalendar}
        onNavigateToWorkOrders={onOpenWorkOrders}
        onNavigateToReceipts={onOpenReceipts}
      />
    );
    return wrap(content);
  }

  if (view === 'platform-management') {
    return (
      <PlatformManagementDashboard
        onBack={onGoBack}
        userEmail={user?.email}
      />
    );
  }

  if (view === 'direct-booking' && user) {
    const effectiveSlug = storefrontProfile?.slug || (
      user.businessName 
        ? user.businessName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') 
        : `empresa-${user.id.slice(0, 6)}`
    );

    const resolvedLogo = resolveCompanyLogo(user, storefrontProfile);
    const baseProfile: StorefrontProfile = storefrontProfile || {
      id: 'prof_' + user.id,
      userId: user.id,
      slug: effectiveSlug,
      displayName: user.businessName || user.ownerName || 'Meu Estabelecimento',
      tagline: user.profession || 'Atendimento Profissional',
      bio: '',
      yearsExperience: '',
      nicheBadges: [user.niche || 'Atendimento Especializado'],
      whatsappNumber: user.phone || '',
      addressDisplay: user.businessAddress ? `${user.businessAddress}${user.businessNumber ? ', ' + user.businessNumber : ''}` : '',
      accentColor: '#10b981',
      isPublished: true,
      viewCount: 0,
      portfolioItems: []
    };

    const effectiveProfile: StorefrontProfile = {
      ...baseProfile,
      displayName: storefrontProfile?.displayName || user.businessName || user.ownerName || baseProfile.displayName,
      accentColor: storefrontProfile?.accentColor || baseProfile.accentColor || '#10b981',
      logoUrl: resolvedLogo || baseProfile.logoUrl || undefined
    };

    const directBookingServices: DirectBookingService[] = (services || []).map(s => ({
      id: s.id,
      name: s.name,
      category: s.type === 'hourly' ? 'Serviços por Hora' : 'Tabela de Serviços',
      price: Number(s.value || 0),
      durationMinutes: 60,
      description: s.description || undefined,
      promo: s.promo
    }));

    return (
      <DirectBookingPage
        profile={effectiveProfile}
        nicheKey={user.niche?.toLowerCase().includes('eletric') ? 'eletricista' : user.niche?.toLowerCase().includes('ar') || user.niche?.toLowerCase().includes('hvac') || user.niche?.toLowerCase().includes('clima') ? 'hvac' : 'estetica'}
        services={directBookingServices}
        staffList={staffMembers}
        existingAppointments={appointments}
        showDemoSwitcher={false}
        onBookAppointment={onSaveAppointment}
        onBack={onGoBack}
      />
    );
  }

  if (view === 'pipeline' && user) {
    const pipelineItems: PipelineItem[] = [
      ...quotes.map(q => {
        let stage: PipelineItem['stage'] = 'draft';
        if (q.status === 'sent') stage = 'sent';
        else if (q.status === 'approved') stage = 'approved';
        else if (q.status === 'in_progress') stage = 'in_progress';
        else if (q.status === 'completed') stage = 'completed';
        else if (q.status === 'paid') stage = 'paid';

        return {
          id: q.id,
          quoteId: q.id,
          title: q.rawServiceName || 'Orçamento de Serviço',
          clientName: q.clientName || 'Cliente',
          clientPhone: q.clientPhone,
          nicheKey: user.niche || 'Geral',
          finalPrice: q.finalPrice || q.finalSuggestedPrice || 0,
          stage,
          updatedAt: q.createdAt ? new Date(q.createdAt).toLocaleDateString('pt-BR') : 'Hoje'
        };
      }),
      ...workOrders.map(wo => {
        let stage: PipelineItem['stage'] = 'in_progress';
        if (wo.status === 'open') stage = 'approved';
        else if (wo.status === 'in_progress') stage = 'in_progress';
        else if (wo.status === 'completed') stage = 'completed';
        else if (wo.status === 'canceled') stage = 'draft';

        return {
          id: wo.id,
          workOrderId: wo.id,
          title: wo.title || 'Ordem de Serviço',
          clientName: wo.clientName || 'Cliente',
          clientPhone: wo.clientPhone,
          nicheKey: wo.nicheKey || user.niche || 'Geral',
          finalPrice: wo.finalPrice || 0,
          stage,
          updatedAt: wo.createdAt ? new Date(wo.createdAt).toLocaleDateString('pt-BR') : 'Hoje'
        };
      })
    ];

    return (
      <ManagementDashboardHybrid
        items={pipelineItems}
        onMoveStage={async (itemId, newStage) => {
          const targetQuote = quotes.find(q => q.id === itemId);
          if (targetQuote) {
            const statusMap: Record<PipelineItem['stage'], QuoteStatus> = {
              draft: 'draft',
              sent: 'sent',
              approved: 'approved',
              in_progress: 'in_progress',
              completed: 'completed',
              paid: 'paid',
              canceled: 'rejected'
            };
            await onSaveQuote({
              ...targetQuote,
              status: statusMap[newStage] || 'draft'
            });
          }
          const targetWo = workOrders.find(w => w.id === itemId);
          if (targetWo) {
            const woStatusMap: Record<string, WorkOrderStatus> = {
              draft: 'open',
              sent: 'open',
              approved: 'open',
              in_progress: 'in_progress',
              completed: 'completed',
              paid: 'completed'
            };
            await onSaveWorkOrder({
              ...targetWo,
              status: woStatusMap[newStage] || 'in_progress'
            });
          }
        }}
        onOpenItem={(item) => {
          if (item.workOrderId) {
            onOpenWorkOrders();
          } else {
            onOpenHistory();
          }
        }}
        onOpenWorkOrder={() => onOpenWorkOrders()}
        onOpenReceipt={() => onOpenReceipts()}
        onCreateNewQuote={() => onOpenChat()}
        onConvertToWorkOrder={() => onOpenWorkOrders()}
        onBack={onGoBack}
      />
    );
  }

  return null;
};
